import {
    get,
    isEqual,
    gt,
    lt,
    gte,
    isString,
    lte,
    startsWith,
    isObject,
} from 'lodash';

/**
 * An operator for the query rules. These are the ways we can compare
 * the value in the rule to the current data within the event. We support
 * the following operators:
 *
 * - eq: Equals - This is the strict equal. (Can be used for anything)
 * - ne: Not Equals - This is the strict not equal. (Can be used for anything)
 * - gt: Greater Than - Checks that the value in the event is greater than the value that is in the query. (Can be used for numbers)
 * - lt: Less Than - Checks that the value in the event is less than the value that is in the query. (Can be used for numbers)
 * - gte: Greater Than or Equal - Checks that the value in the event is greater than or equal to the value that is in the query. (Can be used for numbers)
 * - lte: Less Than or Equal - Checks that the value in the event is less than or equal to the value that is in the query. (Can be used for numbers)
 * - startsWith: Starts With - Checks that the value in the event starts with the substring. (Can be used for strings)
 * - in: In - Checks that the value in the event is in the array of values. (Can be used for arrays)
 * - nin: Not In - Checks that the value in the event is not in the array of values. (Can be used for arrays)
 */
export type EventQueryOperator =
    | 'eq'
    | 'ne'
    | 'gt'
    | 'lt'
    | 'gte'
    | 'lte'
    | 'startsWith'
    | 'contains'
    | 'in'
    | 'nin';

/**
 * This query rule is for flat data. This is used for data on the event that are primitives or
 * arrays.
 */
export interface EventQueryRule {
    /**
     * The value to compare to the event data to. Depending on the value, only
     * certain operators can be used. For example, if the value is a string, you can use
     * eq, ne, startsWith.
     */
    value: boolean | string | number | any[] | object;

    /**
     * An operator for the query rules. These are the ways we can compare
     * the value in the rule to the current data within the event. We support
     * the following operators:
     *
     * - eq: Equals - This is the strict equal. (Can be used for anything)
     * - ne: Not Equals - This is the strict not equal. (Can be used for anything)
     * - gt: Greater Than - Checks that the value in the event is greater than the value that is in the query. (Can be used for numbers)
     * - lt: Less Than - Checks that the value in the event is less than the value that is in the query. (Can be used for numbers)
     * - gte: Greater Than or Equal - Checks that the value in the event is greater than or equal to the value that is in the query. (Can be used for numbers)
     * - lte: Less Than or Equal - Checks that the value in the event is less than or equal to the value that is in the query. (Can be used for numbers)
     * - startsWith: Starts With - Checks that the value in the event starts with the substring. (Can be used for strings)
     * - in: In - Checks that the value in the event is in the array of values. (Can be used for arrays)
     * - nin: Not In - Checks that the value in the event is not in the array of values. (Can be used for arrays)
     */
    operator: EventQueryOperator;
}

/**
 * This is an interface specifically for nested data for the value. This is useful
 * for data that is nested in the payload. For example, if you have a payload that looks like this:
 * ```ts
 * {
 *    user: {
 *      name: 'John Doe',
 *    }
 * }
 * ```
 *
 * You can set the path to `payload.user.name` and the value to `John Doe` and the operator to `eq`.
 */
export interface EventQueryRuleData {
    /**
     * The value to compare to the event data to. Depending on the value, only
     * certain operators can be used. For example, if the value is a string, you can use
     * eq, ne, startsWith.
     */
    value: boolean | string | number | any[];

    /**
     * An operator for the query rules. These are the ways we can compare
     * the value in the rule to the current data within the event. We support
     * the following operators:
     *
     * - eq: Equals - This is the strict equal. (Can be used for anything)
     * - ne: Not Equals - This is the strict not equal. (Can be used for anything)
     * - gt: Greater Than - Checks that the value in the event is greater than the value that is in the query. (Can be used for numbers)
     * - lt: Less Than - Checks that the value in the event is less than the value that is in the query. (Can be used for numbers)
     * - gte: Greater Than or Equal - Checks that the value in the event is greater than or equal to the value that is in the query. (Can be used for numbers)
     * - lte: Less Than or Equal - Checks that the value in the event is less than or equal to the value that is in the query. (Can be used for numbers)
     * - startsWith: Starts With - Checks that the value in the event starts with the substring. (Can be used for strings)
     * - in: In - Checks that the value in the event is in the array of values. (Can be used for arrays)
     * - nin: Not In - Checks that the value in the event is not in the array of values. (Can be used for arrays)
     */
    operator: EventQueryOperator;

    /**
     * The path to the data in the payload. This is useful for nested data.
     */
    path: string;
}
/**
 * This is a query rule that is used to filter events based on the rules.
 * These rules are used by the selector factory to create a custom selector
 * for a given query. You can leverage this by the `filteredEvents` select and passing the query
 * then wrapping it in a `toSignal.` For example, to get all handled events you can do the following:
 *
 * ```ts
 *  readonly isAllHandled: Signal<boolean> = toSignal(
 *       this.select(
 *           this.filteredEvents$({ handled: { value: false, operator: 'eq' } }),
 *           (events: EventInfo[]): boolean => {
 *               return events.length <= 0;
 *           },
 *       ),
 *   );
 *  ```
 *
 */
export interface EventQuery {
    /**
     * The name of the event. This allows you to only filter events for a given event name.
     *
     * Supports only: eq, ne, startsWith
     */
    name?: EventQueryRule;
    /**
     * The payload of the event. This allows for leveraging the path
     * information for comparison.
     *
     * Supports: all depending what the path resovles to.
     */
    payload?: EventQueryRuleData[];
    /**
     * The date in milliseconds of when the event was fired. This is useful for
     * filtering events that happened after a certain time in the lifecycle.
     *
     * Supports: eq, ne, gt, lt, gte, lte
     */
    fired?: EventQueryRule;

    /**
     * Determines if the evennt was handled. This will say if the event has been consumed
     * by a handler. Useful for tracking events that have not been handled and to see to run it.
     *
     * Supports: eq, ne
     */
    handled?: EventQueryRule;
}

/**
 * This is the event information that is stored in the queue.
 * It has the payload and the name of the event.
 * It has private properties to indicate when the event was fired and if it was handled.
 */
export interface EventInfo {
    /**
     * The name of the event. This will act as both the topic and the name of the event.
     * This will also be used to identify the handlers that are listening for this event.
     */
    name: string;
    /**
     * The payload of the event. This is additional data needed to fire the event.
     * Currently, the payload is a string, number, array, or object.
     */
    payload?: string | number | any[] | object;
    /**
     * The date the event was fired. This is useful for tracking the order of events
     * and also figuring out time between events.
     */
    fired?: number;
    /**
     * The date the event was registered. This is mostly used to figure out how long
     * it took to add it to the cue and when it was fired. We could leverage this in the
     * future for events that hang.
     */
    registered?: number;
    /**
     * Determines if this event has been handled. In most cases, a handled event means
     * it was called by a callback function in a handler.
     */
    handled?: boolean; // If the event was handled
    /**
     * The handler id and the options passed in for each handler that handled this event.
     * This is useful for debugging and tracking the handlers that were called.
     */
    handlers?: IqEventBusBrokerHandlerOptions & { handlerId?: string }[]; // The handlers that this event fired

    /**
     * If the event is derived from a source event -- an event that is fired from another event listener -- this
     * the event id of the node tha fired this event.
     */
    parentId?: string;

    /**
     * This is metadata that can be added to the event. This is useful for tracking. 
     * A common use case is to add section, dashboard, and component ids. 
     */
    meta?: Record<string, any>;
}

/**
 * This is a linked list node that is used to store the events in the queue.
 * It also has a topic and offset to indicate when this exact action was fired
 * based on the first.
 */
export interface IqEventBusNode {
    /**
     * The id of the event
     */
    id: string;
    /**
     *  The topic of the event
     */
    topic: string;
    /**
     * The event information
     */
    evt: EventInfo;
    /**
     * The next event in the queue
     */
    nextEvent?: IqEventBusNode;
    /**
     * The topic offset of this specific event to indicate the order of the events.
     */
    offset?: number;
}

/**
 * This state leverages a linked list approach to store events to
 * ensure that the order of events is maintained.
 *
 * The offsets are used to keep track of the order of events for a given topic.
 * This way all events are in order based on their order of appending and the
 * order of the topics.
 *
 * Currently, the topic is the name of the event.
 *
 * Based on this approach: https://github.com/trekhleb/javascript-algorithms/tree/master/src/data-structures/linked-list
 */
export interface IqEventBusQueueState {
    /**
     * The head of the linked list
     */
    head?: IqEventBusNode;
    /**
     * The tail of the linked list
     */
    tail?: IqEventBusNode;
    /**
     * The current offsets. This is for ease of use so we don't need
     * to iterate to find them.
     */
    offsets: Record<string, number>;
}

/**
 * This is the options for the handler. This is decorative data that let's us change
 * or monitor the behavior of the handler. In the future, we can leverage these options and the
 * data to trace how an event went through our system.
 *
 * @param T The data that is passed to the handler. This is if a user wants to type their decorated data for the handler.
 */
export interface IqEventBusBrokerHandlerOptions<T = any> {
    /**
     * Determines if the handler should retrieve the last event that was fired and act on it.
     *
     * If the handler is also once, this will fire it both.
     *
     * ? Should this be defaulted to true?
     */
    immediate?: boolean; // If the handler should be called immediately. Defaults to true
    /**
     * If the handler should only be called once
     */
    once?: boolean;

    /**
     * The id of where this was registered. This can be useful
     * if you want to have the ability to connect it to a component
     * or service.
     */
    parentId?: string;

    /**
     * Handler data
     */
    data?: T;
}

/**
 * This is the information for the broker handler. We leverage this to set up the handler and
 * keep track of the status of the handler.
 */
export interface IqEventBusBrokerHandler {
    /**
     * Name of the event to listen for. This is how we register specific events
     * to be consumed and called by the callback.
     */
    name: string;
    /**
     * The call back function that is called when the event is fired. This only gets called when we handle
     * an event the matches the name property
     *
     * @param payload The payload of the event. This is the data that is passed to the handler when the event is fired.
     * @returns void
     */
    callback: (payload?: any) => void;
    /**
     * The options for the handler. This is decorative data that let's us change the behavior and data of the handler
     * to track it.
     */
    opts: IqEventBusBrokerHandlerOptions;
    /**
     * A unique id for the handler added by the state service. This is more for internal tracking and making sure we keep these
     * unique.
     */
    id: string;

    /**
     * The time the handler was registered. This is to help us track when
     * this handler was added to the system.
     */
    registered: number;
    /**
     * The last time the handler was fired. This is to help us track when
     * the handler was last called.
     */
    lastFired: number;
    /**
     * The time in milliseconds that it was removed. This is to help us know
     * how long the handler was in the system.
     */
    removedInMs?: number;
    /**
     * Determines if the handler has been removed. We use this instead of removing the
     * handler from the state because we might want to keep track of this for auditing/logging.
     */
    removed?: boolean; // If the handler has been removed
}

/**
 * This is the state for the event broker handler. This keeps track of all the handlers in an array.
 */
export interface IqEventBrokerQueueState {
    /**
     * Current handlers that are registered for this session.
     */
    handlers: IqEventBusBrokerHandler[];
}

/**
 * A saved event name to use for communicating components that use the custom event in the
 * service.
 *
 * ! This should never be used outside of this library
 */
export const ServiceConsumerEventName: string = 'iq-event-bus-consumer-service';

/**
 * Used to get the last called value from the event bus.
 */
export const ServiceConsumerEventNameImmediate: string =
    'iq-event-bus-consumer-service-immediate';

/**
 * A saved event name to use for communicating components that use the custom event. This is
 * to listen for custom components that leverage the event bus.
 */
export const CustomElementConsumerEventName =
    'iq-event-bus-consumer-custom-element';

export const matchRule = (
    eventProperty: any,
    queryObj: EventQueryRule,
): boolean => {
    const comparisons: Record<
        EventQueryOperator,
        (comparison, comparator) => boolean
    > = {
        eq: (comparison, comparator) => isEqual(comparison, comparator),
        ne: (comparison, comparator) => {
            return !isEqual(comparison, comparator);
        },
        gt: (comparison, comparator) => gt(comparison, comparator),
        lt: (comparison, comparator) => lt(comparison, comparator),
        gte: (comparison, comparator) => gte(comparison, comparator),
        lte: (comparison, comparator) => lte(comparison, comparator),
        in: (comparison, comparator) => comparator.includes(comparison),
        nin: (comparison, comparator) => !comparator.includes(comparison),
        startsWith: (comparison: any, comparator: any): boolean => {
            return isString(comparison) && isString(comparator)
                ? startsWith(comparison, comparator)
                : false;
        },
        contains: (comparison: any, comparator: any) =>
            contains(comparison, comparator),
    };

    return comparisons[queryObj.operator](eventProperty, queryObj.value);
};

export const createCacheEventName = (name: string): string => `${name}:cache`;

export const matchQuery = (
    evt: EventInfo,
    query: EventQuery,
    operator: string = 'AND',
): boolean => {
    const match = {
        AND: (query: EventQuery): boolean => {
            return Object.keys(query).every((key): boolean => {
                return key === 'payload'
                    ? query.payload.every(
                          (rule: EventQueryRuleData): boolean => {
                              return matchRule(
                                  get(evt.payload, rule.path),
                                  rule,
                              );
                          },
                      )
                    : matchRule(evt[key], query[key]);
            });
        },
        OR: (query: EventQuery): boolean => {
            return Object.keys(query).some((key): boolean => {
                return key === 'payload'
                    ? query.payload.some(
                          (rule: EventQueryRuleData): boolean => {
                              return matchRule(
                                  get(evt.payload, rule.path),
                                  rule,
                              );
                          },
                      )
                    : matchRule(evt[key], query[key]);
            });
        },
        NOT: (query: EventQuery): boolean => {
            return !Object.keys(query).some((key): boolean => {
                return key === 'payload'
                    ? query.payload.some(
                          (rule: EventQueryRuleData): boolean => {
                              return matchRule(
                                  get(evt.payload, rule.path),
                                  rule,
                              );
                          },
                      )
                    : matchRule(evt[key], query[key]);
            });
        },
    };

    return !!match[operator] ? match[operator](query) : match.AND(query);
};

/**
 * This is a custom operator that checks if the comparator contains the comparison.
 *
 * Example:
 * Comparison Object:
 *
 * ```json
 * {
 *   "test": "test",
 *   "test2": "test2",
 *   "test3.test4": true,
 * }
 * ```
 * Comparator Object:
 * ```json
 * {
 *   "test": "test",
 *   "test2": "test2",
 *   "test3":{
 *      "test4": true,
 *   }
 * }
 * ```
 *
 * This will return true because the comparator contains the comparison.
 *
 * @param comparison - a flat object where the keys are the path and the value is the one to test
 * @param comparator - the object to test against
 * @returns true if the comparator contains the comparison
 */
function contains(comparison: any, comparator: any): boolean {
    if (!isObject(comparator)) return false;

    return Object.keys(comparison).every((key) => {
        return isEqual(get(comparator, key), get(comparison, key));
    });
}
